import { useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { requireGlobalCapability } from '@/lib/route-guards';
import { warm, warmBackground } from '@/api/loaders';
import { adminInvoiceById, adminInvoices } from '@/api/resources';
import {
  invoicesListQuery,
  parseInvoicesSearch,
  updateInvoicesSearch,
} from '@/lib/invoicing-view';
import type { InvoicesSearch } from '@/lib/invoicing-view';
import { InvoicesView } from '@/screens/admin-invoices';

/**
 * The period range, the state filter and the selected invoice all live in the
 * URL. The first three GATE THE READ — they are the cache key — so none may be
 * a `useState`: a gate that only exists once the component has mounted is
 * unreachable from the loader that runs before it. The selected invoice makes
 * a review linkable, which for a "please approve this" message is the point.
 */
export const Route = createFileRoute('/admin/invoices')({
  // Matches `require_billing_access` in the API: Full `billing_manage` AND
  // an unscoped admin. An invoice covers every production.
  beforeLoad: requireGlobalCapability('billing_manage', 'full'),
  validateSearch: (search: Record<string, unknown>) => parseInvoicesSearch(search),
  loaderDeps: ({ search }) => ({
    from: search.from,
    to: search.to,
    status: search.status,
    invoice: search.invoice,
  }),
  // Search-driven route: no skeleton for an in-screen navigation (router.ts).
  pendingMs: Infinity,
  loader: ({ context, deps }) => {
    if (deps.invoice) {
      // Realtime slots are born stale; the drawer owns its loading state.
      warmBackground(context.queryClient, adminInvoiceById(deps.invoice));
    }
    // The list IS the screen: block. `invoicesListQuery` is the same function
    // the screen calls, so the loader and the observer hash the same key.
    return warm(context.queryClient, adminInvoices(invoicesListQuery(deps)));
  },
  component: InvoicesRoute,
});

function InvoicesRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onSearchChange = useCallback(
    (patch: Partial<InvoicesSearch>, opts?: { replace?: boolean }) =>
      void navigate({
        search: (prev) => updateInvoicesSearch(prev, patch),
        replace: opts?.replace,
      }),
    [navigate],
  );
  return <InvoicesView search={search} onSearchChange={onSearchChange} />;
}
